import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { AUDIT_ACTIONS } from '../services/auditLog'

const AuditLogPill = styled.button`
  height: 40px;
  padding: 0 1.25rem;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  z-index: ${theme.zIndex.navigation};
  box-shadow: 0 2px 8px ${theme.color.shadow.dark};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #b8002b;
    transform: translateY(-1px);
  }
`

const AuditLogModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const AuditLogContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 8px 32px ${theme.color.shadow.dark};
`

const AuditLogHeader = styled.div`
  background: ${theme.color.accent.primary};
  color: ${theme.color.text.light};
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 600;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.color.text.light};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const LogList = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
`

const LogItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${theme.color.border.light};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${theme.color.background.lightGrey};
  }
`

const LogDetails = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.base};
  font-weight: 500;
  margin-bottom: 0.5rem;
  line-height: 1.4;
`

const LogMeta = styled.div`
  font-size: 1.1rem;
  color: ${theme.color.text.grey};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const LogAdmin = styled.span`
  font-weight: 500;
`

const LogDate = styled.span`
  font-size: 1rem;
`

const LogTarget = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  background: ${theme.color.background.lightGrey};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`

const GiftCardCode = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
`

const MilestoneBadge = styled.span`
  font-size: 1rem;
  background: #fff3e0;
  color: #e65100;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
`

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${theme.color.text.grey};
  font-style: italic;
  font-size: 1.2rem;
`

const ErrorMessage = styled.div`
  padding: 1.5rem;
  color: #d70036;
  background: #ffe6e6;
  margin: 1rem;
  border-radius: 4px;
  font-size: 1.2rem;
`

const EmptyMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${theme.color.text.grey};
  font-size: 1.2rem;
`

const DateFilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: ${theme.color.background.lightGrey};
  border-bottom: 1px solid ${theme.color.border.light};
  align-items: center;
  flex-wrap: wrap;
`

const DateInput = styled.input`
  padding: 0.5rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1rem;
`

const DateLabel = styled.label`
  font-size: 1.1rem;
  color: ${theme.color.text.grey};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ExportButton = styled.button`
  background: #2e7d32;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;

  &:hover {
    background: #1b5e20;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const SearchInput = styled.input`
  padding: 0.5rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1rem;
  width: 200px;
`

interface AuditLogEntry {
  id: number
  user_email: string
  tab_name: string
  action: string
  details: {
    milestone?: string
    gift_card_code?: string
    gift_card_balance?: string
  } | null
  admin_user: string
  created_at: string
}

const GiftCardAuditLog: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')

  const fetchLogs = async (filterStartDate?: string, filterEndDate?: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const queryParams = new URLSearchParams({
        action: AUDIT_ACTIONS.GIFT_CARD_ISSUED,
        limit: '1000'
      })

      if (filterStartDate) {
        queryParams.append('startDate', new Date(filterStartDate).toISOString())
      }
      if (filterEndDate) {
        // Set end date to end of day
        const endOfDay = new Date(filterEndDate)
        endOfDay.setHours(23, 59, 59, 999)
        queryParams.append('endDate', endOfDay.toISOString())
      }

      const response = await fetch(`/api/audit-log?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.statusText}`)
      }

      const data = await response.json()
      // API returns { success: true, logs: [...] }
      const logsArray = data.logs || []
      // Sort by date (most recent first) - though API already sorts by created_at DESC
      const sortedLogs = logsArray.sort((a: AuditLogEntry, b: AuditLogEntry) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      setLogs(sortedLogs)
    } catch (err) {
      console.error('Failed to fetch gift card audit logs:', err)
      setError(`Failed to load gift card issuance history: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && logs.length === 0) {
      fetchLogs(startDate, endDate)
    }
  }, [isOpen])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Unknown date'
    }
  }

  const formatMilestone = (milestone: string | undefined) => {
    if (!milestone) return 'Unknown'
    return `${milestone} classes`
  }

  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false)
    }
  }

  const handleRefresh = () => {
    setLogs([])
    fetchLogs(startDate, endDate)
  }

  const handleDateFilter = () => {
    setLogs([])
    fetchLogs(startDate, endDate)
  }

  // Filter logs by search query (email or user ID)
  const filteredLogs = logs.filter(log => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase().trim()
    return log.user_email?.toLowerCase().includes(query)
  })

  const formatDateForCSV = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toISOString().replace('T', ' ').substring(0, 19)
    } catch {
      return 'Unknown'
    }
  }

  const exportToCSV = () => {
    if (filteredLogs.length === 0) return

    const headers = ['Date/Time', 'Recipient Email', 'Milestone', 'Gift Card Code', 'Balance', 'Issued By']

    const csvRows = filteredLogs.map(log => [
      formatDateForCSV(log.created_at),
      log.user_email,
      log.details?.milestone || '',
      log.details?.gift_card_code || '',
      log.details?.gift_card_balance || '',
      log.admin_user
    ])

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    // Create filename with date range
    const now = new Date()
    const timestamp = now.toISOString().split('T')[0]
    let filename = `gift_card_issuances_${timestamp}`
    if (startDate) filename += `_from_${startDate}`
    if (endDate) filename += `_to_${endDate}`
    filename += '.csv'

    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <AuditLogPill onClick={() => setIsOpen(true)} title="Gift Card Issuance Log">
        <span style={{ fontSize: '1.1rem' }}>🎁</span>
        Gift Card Log
      </AuditLogPill>

      <AuditLogModal isOpen={isOpen} onClick={handleModalClick}>
        <AuditLogContent>
          <AuditLogHeader>
            <HeaderTitle>Gift Card Issuance Log</HeaderTitle>
            <div>
              <CloseButton onClick={handleRefresh} title="Refresh" style={{ marginRight: '0.5rem' }}>↻</CloseButton>
              <CloseButton onClick={() => setIsOpen(false)}>×</CloseButton>
            </div>
          </AuditLogHeader>

          <DateFilterContainer>
            <DateLabel>
              From:
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </DateLabel>
            <DateLabel>
              To:
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </DateLabel>
            <CloseButton
              onClick={handleDateFilter}
              title="Apply Filter"
              style={{ color: theme.color.text.base, fontSize: '1.2rem' }}
            >
              Filter
            </CloseButton>
            <SearchInput
              type="text"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <ExportButton onClick={exportToCSV} disabled={filteredLogs.length === 0}>
              Export CSV
            </ExportButton>
          </DateFilterContainer>

          {isLoading ? (
            <LoadingMessage>Loading gift card issuances...</LoadingMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : filteredLogs.length === 0 ? (
            <EmptyMessage>{searchQuery ? 'No matching gift card issuances found.' : 'No gift card issuances found.'}</EmptyMessage>
          ) : (
            <LogList>
              {filteredLogs.map((log) => (
                <LogItem key={log.id}>
                  <LogDetails>
                    <MilestoneBadge>{formatMilestone(log.details?.milestone)}</MilestoneBadge>
                    {log.details?.gift_card_code && (
                      <GiftCardCode>{log.details.gift_card_code}</GiftCardCode>
                    )}
                    {log.details?.gift_card_balance && (
                      <span style={{ marginLeft: '0.5rem', color: '#2e7d32', fontWeight: 600 }}>
                        ${log.details.gift_card_balance}
                      </span>
                    )}
                  </LogDetails>
                  <LogMeta>
                    <div>
                      <LogAdmin>Issued by: {log.admin_user}</LogAdmin>
                    </div>
                    <div>
                      <LogTarget>To: {log.user_email}</LogTarget>
                    </div>
                    <LogDate>{formatDate(log.created_at)}</LogDate>
                  </LogMeta>
                </LogItem>
              ))}
            </LogList>
          )}
        </AuditLogContent>
      </AuditLogModal>
    </>
  )
}

export default GiftCardAuditLog
